import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  Grid, 
  Container, 
  Typography, 
  FormControl, 
  Input, 
  InputAdornment, 
  Button, 
  ButtonGroup, 
  Paper, 
  Skeleton,
  Menu, 
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { User, Unauthorized } from '../sections/@dashboard/manageUser';
import { Policy } from '../sections/@dashboard/managePolicy';
import { Group } from '../sections/@dashboard/manageGroup';
import { Placeholder, Toaster } from '../sections/@dashboard/attributes';
import { ProfileCard } from '../components/skeleton';
import { useUserViewQuery, useGetPolicyQuery, useGetGroupQuery } from '../services/dvagent';
import { getToken, getUsername, getAdmin } from '../services/LocalStorageService';
import Iconify from '../components/iconify';

const userColumns = [
  { field: 'serialNumber', headerName: 'S. No.', width: 100 },
  { 
    field: 'first_name', 
    headerName: 'Name', 
    flex: 1, 
    renderCell: (params) => (
      <RouterLink 
        to="/cloud/update-user"
        state={params.row.email}
        key={params.row.id}
        style={{ textDecoration: 'none', color: '#000', fontWeight: 'bold' }}
      >
        {`${params.row.first_name} ${params.row.last_name}` || 'N/A'}  
      </RouterLink>
    ),
  },
  { field: 'email', headerName: 'Email', flex: 1 },
  { field: 'is_active', headerName: 'Is Active', flex: 1 },
  { field: 'is_superuser', headerName: 'Is Superuser', flex: 1 },
  { field: 'created_by_name', headerName: 'Created By', flex: 1 },
  { field: 'created_on', headerName: 'Created On', flex: 1 },
];

const policyColumns = [
  { field: 'serialNumber', headerName: 'S. No.', width: 100 },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    renderCell: (params) => (
      <RouterLink 
        to="/cloud/update-policy"
        state={params.row.id}
        key={params.row.id}
        style={{ textDecoration: 'none', color: '#000', fontWeight: 'bold' }}
      >
      {params.row.name}
      </RouterLink>
      )
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 1,
    renderCell: (params) => `${params.row.createdBy?.first_name || ''} ${params.row.createdBy?.last_name || ''}`.trim() || 'N/A',
  },
  { field: 'createdOn', headerName: 'Created On', flex: 1 },
  {
    field: 'modifiedBy',
    headerName: 'Modified By',
    flex: 1,
    renderCell: (params) => `${params.row.modifiedBy?.first_name || ''} ${params.row.modifiedBy?.last_name || ''}`.trim() || 'N/A',
  },
  { field: 'modifiedOn', headerName: 'Modified On', flex: 1 },
];

const groupColumns = [
  { field: 'serialNumber', headerName: 'S. No.', width: 100 },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    renderCell: (params) => (
      <RouterLink 
        to="/cloud/update-group"
        state={params.row.id}
        key={params.row.id}
        style={{ textDecoration: 'none', color: '#000', fontWeight: 'bold' }}
      >
      {params.row.name}
      </RouterLink>
      )
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 1,
    renderCell: (params) => `${params.row.createdBy?.first_name || ''} ${params.row.createdBy?.last_name || ''}`.trim() || 'N/A',
  },
  { field: 'createdOn', headerName: 'Created On', flex: 1 },
  {
    field: 'modifiedBy',
    headerName: 'Modified By',
    flex: 1,
    renderCell: (params) => `${params.row.modifiedBy?.first_name || ''} ${params.row.modifiedBy?.last_name || ''}`.trim() || 'N/A',
  },
  { field: 'modifiedOn', headerName: 'Modified On', flex: 1 },
];

const paginationModel = { page: 0, pageSize: 10 };

export default function Users() {
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const {state} = useLocation();
  const [user, setUser] = useState([]);
  const [policy, setPolicy] = useState([]);
  const [group, setGroup] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(state.dropdownState ?? 'Users');
  const [selectedIcon, setSelectedIcon] = useState('material-symbols:group-add-outline-rounded');
  const [view, setView] = useState('grid');
  const { data:groupData, isSuccess:groupIsSuccess, error:groupError } = useGetGroupQuery({"token":getToken(), "location":location});
  const { data:policyData, isSuccess:policyIsSuccess, error:policyError } = useGetPolicyQuery({"token":getToken(), "location":location});
  const { data:userData, isSuccess:userIsSuccess, error:userError } = useUserViewQuery({"token":getToken(), "location":location});
  const [anchorEl, setAnchorEl] = useState(null);
  const [toasterData, setToasterData] = useState({
    trigger: false,
    status: true,
    symbol: '✔',
    message: '',
  });
  const open = Boolean(anchorEl);
  
  useEffect(() => {
    setLoading(false);

    if (userError || policyError || groupError) {
      const statusCode =
        userError?.status || policyError?.status || groupError?.status;

      if (statusCode === 401) {
        // showToast(false, 'ⓘ', 'Unauthorized User');
      } else {
        showToast(false, 'ⓘ', 'Something went wrong, server error');
      }
    }

    if (userData && userIsSuccess) {
      setUser(userData.data);
      setLoading(true);
    }

    if (policyData && policyIsSuccess) {
      setPolicy(policyData.data);
    }

    if (groupData && groupIsSuccess) {
      setGroup(groupData.data);
    }

    const timeout = setTimeout(() => {
      setLoading(true);
    }, 5000);

    return () => clearTimeout(timeout); // cleanup on unmount
  }, [userData, userIsSuccess, userError, policyData, policyError, policyIsSuccess, groupData, groupError, groupIsSuccess]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredUsers = user.filter(
    (item) =>
    `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const filteredPolices = policy.filter(
    (item) =>
    `${item.name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );


  const filteredGroups = group.filter(
    (item) =>
    `${item.name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const userRows = filteredUsers.map((row, index) => ({
    ...row,
    serialNumber: index + 1,
  }));

  const policyRows = filteredPolices.map((row, index) => ({
    ...row,
    serialNumber: index + 1,
  }));

  const groupRows = filteredGroups.map((row, index) => ({
    ...row,
    serialNumber: index + 1,
  }));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownSelect = (option, icon) => {
    setSelected(option)
    setSelectedIcon(icon)
    setLoading(false);
    if (userData && userIsSuccess) {
      setUser(userData.data);
      setLoading(true);
    }
    if (policyData && policyIsSuccess) {
      setPolicy(policyData.data)
    }
    if (groupData && groupIsSuccess) {
      setGroup(groupData.data)
    }
    setTimeout(() => {
      setLoading(true);
    }, 1000);
    handleMenuClose();
  };

  const showToast = (status, symbol, message) => {
    setToasterData({
      trigger: true,
      status,
      symbol,
      message,
    });

    setTimeout(() => {
      setToasterData((prev) => ({
        ...prev,
        trigger: false,
      }));
    }, 5000); 
  };

  return (
    <>
      <Helmet>
        <title> DV Agent </title>
      </Helmet>
      {toasterData.trigger && (
        <Toaster
          trigger={toasterData.trigger}
          status={toasterData.status}
          symbol={toasterData.symbol}
          message={toasterData.message}
        />
      )}
      <Container maxWidth="xxl">
        {loading ? (
          <>
            <Grid container spacing={3} sx={{mb : 2}} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  Hi {loading ? getUsername() : <></>}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={6}>
                <FormControl
                  variant="standard"
                  fullWidth
                >
                  <Input
                    id="standard-adornment-amount"
                    variant="standard"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search"
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>

            <ButtonGroup
              variant="outlined"
              aria-label="Disabled button group"
              sx={{ mb: 2 }}
            >
              <Button
                onClick={() => handleViewChange('grid')}
                variant={view === 'grid' ? 'contained' : 'outlined'}
              >
                <Iconify icon="line-md:grid-3-filled" />
                &nbsp;Grid
              </Button>
              <Button
                onClick={() => handleViewChange('list')}
                variant={view === 'list' ? 'contained' : 'outlined'}
              >
                <Iconify icon="line-md:list-3-filled" />
                &nbsp;List
              </Button> 
              <Button
                onClick={handleMenuOpen}
                variant="contained"
                aria-controls={open ? 'dropdown-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                <Iconify icon={selectedIcon} mr={1}/>{selected}<Iconify icon={'gridicons:dropdown'} ml={1}/>
              </Button>
            </ButtonGroup>
            <Menu
              id="dropdown-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'options-button',
              }}
            >
              <MenuItem onClick={() => handleDropdownSelect('Users', 'material-symbols:group-add-outline-rounded')}>
                <Iconify icon={'material-symbols:group-add-outline-rounded'} mr={1}/>Users
              </MenuItem>
              <MenuItem onClick={() => handleDropdownSelect('Policies', 'material-symbols:local-police-rounded')}>
                <Iconify icon={'material-symbols-light:policy-rounded'} mr={1}/>Policies
              </MenuItem>
              <MenuItem onClick={() => handleDropdownSelect('Groups', 'fa-solid:users-cog')}>
                <Iconify icon={'fa-solid:users-cog'} mr={1}/>Groups
              </MenuItem>
            </Menu>

            {getAdmin() === 'false' ? <Unauthorized/> :
            view === 'grid' ? (
                selected === 'Users' ? (
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={3}
                      xl={2}
                      key="placeholder"
                      component={RouterLink}
                      to="/cloud/add-user"
                      style={{ textDecoration: 'none' }}
                    >
                      <Placeholder />
                    </Grid>
                    {filteredUsers.map((item) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        xl={2}
                        to="/cloud/update-user"
                        state={item.email}
                        component={RouterLink}
                        key={item.id}
                        style={{ textDecoration: 'none' }}
                      >
                        <User
                          profile="../assets/app/default-user.gif"
                          name={`${item.first_name} ${item.last_name}`}
                          email={item.email}
                          verified={item.is_superuser}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : selected === 'Policies' ? (
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={3}
                      xl={2}
                      key="placeholder"
                      component={RouterLink}
                      to="/cloud/add-policy"
                      style={{ textDecoration: 'none' }}
                    >
                      <Placeholder />
                    </Grid>
                    {filteredPolices.map((item) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        xl={2}
                        to={item.is_super ? null : "/cloud/update-policy"}
                        state={item.id}
                        component={item.is_super ? null : RouterLink}
                        key={item.id}
                        style={{ textDecoration: 'none' }}
                      >
                        <Policy
                          profile="../assets/app/policy.gif"
                          name={item.name}
                          isSuper={item.is_super}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      md={3}
                      xl={2}
                      key="placeholder"
                      component={RouterLink}
                      to="/cloud/add-group"
                      style={{ textDecoration: 'none' }}
                    >
                      <Placeholder />
                    </Grid>
                    {filteredGroups.map((item) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        xl={2}
                        to="/cloud/update-group"
                        state={item.id}
                        component={RouterLink}
                        key={item.id}
                        style={{ textDecoration: 'none' }}
                      > 
                        <Group
                          profile="../assets/app/group.gif"
                          name={item.name}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : (
                <Paper>
                  <DataGrid
                    rows={selected === 'Users' ? userRows : selected === 'Policies' ? policyRows.slice(1) : groupRows}
                    columns={selected === 'Users' ? userColumns : selected === 'Policies' ? policyColumns : groupColumns}
                    initialState={{ pagination: { paginationModel } }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    checkboxSelection
                    sx={{
                      border: 0,
                      width: '100%',
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#afb3c7', 
                        color: '#A8A196',          
                        fontSize: '16px',          
                        fontWeight: 'bold',        
                      },
                      '& .MuiDataGrid-columnSeparator': {
                        display: 'none',           
                      },
                    }}
                  />
                </Paper>
            )}
          </>
        ) : (
          <>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  <Skeleton animation="wave" height={40} width="30%" />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={8} lg={6}>
                <FormControl
                  variant="standard"
                  fullWidth
                >
                  <Input
                    id="standard-adornment-amount"
                    variant="standard"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search"
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4} md={4} lg={6}>
                <Typography variant="h6" color="text.secondary">
                  <Skeleton animation="wave" height={60} width="35%" />
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              {Array.from({ length: 20 }).map((_, index) => (
                <Grid item xs={12} sm={4} md={3} xl={2} key={index}>
                  <ProfileCard />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>
    </>
  );
}
